import React, { useEffect, useRef } from "react";

// Shared video time state for synchronization across all Sponsor components
// Persists across component lifecycle so videos resume from last position
let sharedVideoTime = 0;
let sharedVideoUpdateInterval: ReturnType<typeof setInterval> | null = null;
const activeVideoRefs = new Set<HTMLVideoElement>();

const VIDEO_EXTENSIONS = [".mp4", ".webm", ".ogg", ".mov"];

function isVideoFile(filePath: string): boolean {
  const lowerPath = filePath.toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => lowerPath.endsWith(ext));
}

function startSharedTimeUpdater() {
  if (sharedVideoUpdateInterval) return;

  sharedVideoUpdateInterval = setInterval(() => {
    // Use the first active video as the time source
    const firstVideo = activeVideoRefs.values().next().value;
    if (firstVideo && !firstVideo.paused) {
      sharedVideoTime = firstVideo.currentTime;
    }
  }, 100);
}

function stopSharedTimeUpdater() {
  if (sharedVideoUpdateInterval) {
    clearInterval(sharedVideoUpdateInterval);
    sharedVideoUpdateInterval = null;
  }
}

interface SponsorProps {
  fileURL?: string;
}

export const Sponsor: React.FC<SponsorProps> = (props) => {
  const { fileURL } = props;
  const videoRef = useRef<HTMLVideoElement>(null);

  const filePath = (fileURL ?? "")
    .replace("$width", "1920")
    .replace("$height", "144")
    .replace("$screenId", "1");

  const isVideo = filePath ? isVideoFile(filePath) : false;

  // Video synchronization effect
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideo) return;

    activeVideoRefs.add(video);
    startSharedTimeUpdater();

    const handleCanPlay = () => {
      // Start at shared time position
      if (Math.abs(video.currentTime - sharedVideoTime) > 0.5) {
        video.currentTime = sharedVideoTime;
      }
      video.play().catch(() => {
        // Autoplay might be blocked, that's okay
      });
    };

    const handleTimeUpdate = () => {
      // Keep videos synchronized - if this video drifts too far, sync it
      if (Math.abs(video.currentTime - sharedVideoTime) > 0.5) {
        video.currentTime = sharedVideoTime;
      }
    };

    const handleEnded = () => {
      // Loop: restart at beginning
      video.currentTime = 0;
      sharedVideoTime = 0;
      video.play().catch(() => {
        /* empty */
      });
    };

    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("ended", handleEnded);

    // If video is already ready, sync immediately
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      handleCanPlay();
    }

    return () => {
      activeVideoRefs.delete(video);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("ended", handleEnded);

      if (activeVideoRefs.size === 0) {
        stopSharedTimeUpdater();
      }
    };
  }, [isVideo, filePath]);

  return (
    <div
      style={{
        backgroundColor: "black",
        height: 144,
        width: 1200,
      }}
    >
      {filePath && (
        <>
          {isVideo ? (
            <video
              ref={videoRef}
              src={filePath}
              muted
              playsInline
              style={{ height: 144, width: "auto", objectFit: "contain" }}
            />
          ) : (
            <img src={filePath} alt="" />
          )}
        </>
      )}
    </div>
  );
};
